<template>
  <v-row class="text-left full-height">
    <v-col cols="12" sm="12" md="6">
      <v-card class="rounded-lg pa-3">
        <v-card-title class="font-weight-bold">Company Info</v-card-title>
        <div>
          <div class="d-flex justify-space-between full-width">
            <v-img
              v-if="companyPicUrl"
              :src="companyPicUrl"
              max-height="250"
              height="auto"
              width="50%"
              max-width="300px"
              contain
              @click="handleLoadImageSelector"
              class="cursor-pointer ma-3 box-border-global"
            />
            <v-img
              v-else
              src="@/assets/placeholder_image.png"
              max-height="250"
              height="auto"
              width="50%"
              max-width="300px"
              contain
              @click="handleLoadImageSelector"
              class="cursor-pointer ma-3 box-border-global"
            />
            <div class="d-flex flex-column justify-start align-start">
              <v-btn
                v-if="companyPicUrl && !isWhitelabeled"
                medium
                right
                text
                color="red"
                @click="resetImageState(false)"
              >
                Delete Logo
              </v-btn>
              <v-btn
                v-else-if="!isWhitelabeled"
                medium
                right
                text
                color="brandCyan"
                @click="handleLoadImageSelector"
              >
                Upload Logo
              </v-btn>
              <p v-if="isWhitelabeled" class="grey--text ml-4" data-testid="logo-whitelabel-notice">
                This logo is managed by your whitelabel configuration and appears at the top of the
                site, on notices, and in other communications to participants. To request a change,
                contact your One10 client service representative.
              </p>
              <p v-else class="grey--text ml-4">
                Appears at the top of the site, on notices, and other communications to
                participants.
                <br /><br />
                Preferred image size is in an aspect ratio of 2 / 1 or 3 / 1.
                <br /><br />
                Maximum file size is 10MB.
              </p>
              <p v-if="!imageIsPNG && companyPicUrl && companyPicFile" class="red--text ml-4">
                To avoid display issues we suggest a PNG logo file
              </p>
            </div>
          </div>
          <input
            ref="uploader"
            class="d-none"
            type="file"
            accept="image/*"
            @change="handleFileChange"
          />
        </div>
        <v-form
          v-model="form.validCompanyInfo"
          onSubmit="return false;"
          class="full-width d-flex flex-column align-center"
        >
          <v-text-field
            dense
            outlined
            color="brandCyan"
            class="mt-4 full-width"
            v-model="clientName"
            label="Company Name"
            :rules="[(v) => !!v || 'Company name is required']"
          ></v-text-field>
          <v-select
            v-model="industry"
            :items="industries"
            label="Industry"
            item-value="value"
            item-text="name"
            dense
            outlined
            color="brandCyan"
            class="mt-4 full-width"
          ></v-select>
          <v-text-field
            dense
            color="brandCyan"
            class="mt-4 full-width"
            v-model="emailDomain"
            label="Company Email Domain"
            :rules="[
              (v) =>
                !!(!v || (v && !v.includes('@'))) ||
                'Email domain should not include the @ symbol. Example: robin@gmail.com would be entered as gmail.com',
            ]"
            outlined
          ></v-text-field>
          <div class="d-flex flex-column align-center">
            <v-btn
              @click="handleSaveCompanyInfo"
              :disabled="!form.validCompanyInfo"
              :loading="loading.companyInfo"
              depressed
              rounded
              color="brandCyan"
              class="white--text my-4"
              >Save Changes</v-btn
            >
            <p v-if="error.companyInfo" class="error--text">
              {{ error.companyInfo }}
            </p>
          </div>
        </v-form>
        <div></div>
      </v-card>
    </v-col>
    <v-col cols="12" sm="12" md="6">
      <v-card class="rounded-lg pa-3">
        <v-card-title class="font-weight-bold">Company Signup Link and QR Code</v-card-title>
        <v-card-text
          >Your company signup link lets you share a link with co-workers or anyone else in your
          universe you want to invite to join your instance.
        </v-card-text>
        <v-card-text>
          It does not give someone access to a program or budgets. Those are configured
          separately.</v-card-text
        >
        <v-card-text v-if="formattedSignupLink"
          ><a :href="formattedSignupLink" target="_blank">{{ formattedSignupLink }}</a>
          <v-btn class="ml-2 mb-1" icon small @click="handleEditSignupLink">
            <v-icon small>mdi-pencil </v-icon>
          </v-btn></v-card-text
        >
        <qrcode-vue
          v-if="formattedSignupLink"
          :value="formattedSignupLink"
          :size="225"
          level="H"
          class="text-center my-4"
        />
        <div class="d-flex justify-center my-4">
          <v-btn text color="brandCyan" @click="handleCopySignupLink" v-if="formattedSignupLink"
            >Copy QR Link</v-btn
          >
          <v-btn v-else @click="handleEditSignupLink" depressed text color="brandCyan"
            >Generate custom signup link</v-btn
          >
        </div>
        <div></div>
      </v-card>
    </v-col>
    <!-- Dialog for editing a client's onboarding link -->
    <v-dialog v-model="dialog.onboardingToken" width="500" class="dialog">
      <v-card rounded="0" class="d-flex justify-center flex-column pa-6">
        <div class="d-flex justify-space-between align-center mx-2 mb-5">
          <v-card-title class="word-break text-left exit-warning-text">
            {{
              onboardingTokenDraft
                ? "Create your custom signup link"
                : "Edit your custom signup link"
            }}
          </v-card-title>
        </div>
        <v-form v-model="form.validOnboardingLink" onSubmit="return false;" class="full-width">
          <v-card-text class="pt-0 full-width text-left">
            <p v-if="error.onboardingToken" class="red--text text-caption mb-2">
              {{ error.onboardingToken }}
            </p>
            <v-text-field
              label="Custom link"
              dense
              outlined
              placeholder="e.g. dunder-mifflin"
              v-model="onboardingTokenDraft"
              :rules="[
                (v) => !!(!v || (v && v.length < 128)) || 'Your link should be shorter',
                (v) =>
                  !!(!v || (v && v.length > 5)) || 'The link should be at least 6 characters long',
              ]"
              class="input-box mx-auto"
              @keyup.enter="handleSaveOnboardingLink"
            ></v-text-field>
            <div class="full-width text-left">
              <p :class="{ hidden: !formattedSignupLinkEditor }" class="mb-1">
                New link: <b>{{ formattedSignupLinkEditor }}</b>
              </p>
              <span class="grey--text"
                >Note: Your link may be altered to remove unapproved characters</span
              >
            </div>
          </v-card-text>
        </v-form>

        <v-card-actions class="mx-12 d-flex justify-center">
          <v-btn
            class="mr-4"
            color="brandCyan"
            outlined
            rounded
            width="130"
            :disabled="loading.onboardingToken"
            @click="(dialog.onboardingToken = false), (onboardingTokenDraft = null)"
            >Cancel</v-btn
          >
          <v-btn
            class="ml-4 white--text"
            color="brandCyan"
            width="130"
            depressed
            rounded
            @click="handleSaveOnboardingLink"
            :disabled="!form.validOnboardingLink"
            :loading="loading.onboardingToken"
            >Done</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ImageCropper
      v-if="dialog.imageCropper"
      :fileName="companyPicFileName"
      :imageUrl="companyPicCropUrl"
      @close="resetImageState(true)"
      @success="handleSaveCroppedImage"
    />
    <!-- Dialog used for displaying copied url snackbar. -->
    <v-snackbar v-model="dialog.snackbar" :timeout="6000" :multi-line="true" :v-html="snackbarText">
      {{ snackbarText }}

      <template v-slot:action="{ attrs }">
        <v-btn color="blue" text v-bind="attrs" @click="dialog.snackbar = false"> Close </v-btn>
      </template>
    </v-snackbar>
  </v-row>
</template>

<script>
import ImageCropper from "@/components/ImageCropper.vue";

import { industries } from "@/shared_data/data";

import ClientService from "@/services/ClientServiceV2";

import { mapState } from "vuex";
import QrcodeVue from "qrcode.vue";

function initialState() {
  return {
    clientService: new ClientService(),

    // Company pic
    selectCompanyPic: false,
    companyPicUrl: null,
    companyPicFile: null,
    companyPicFileName: null,
    deletedCompanyPic: false,
    companyPicCropUrl: null,
    imageIsPNG: false,
    imageIsPNGTemp: false,

    // Client info
    clientId: null,
    clientName: null,
    emailDomain: null,
    industry: null,
    onboardingToken: null,

    // Static data
    industries,

    // Editing
    onboardingTokenDraft: null,

    // Dialogs, API, and forms
    snackbarText: "Copied URL to clipboard",
    dialog: {
      onboardingToken: false,
      snackbar: false,
      imageSelector: false,
      imageCropper: false,
    },
    form: {
      validOnboardingLink: true,
      validCompanyInfo: true,
    },
    error: {
      onboardingToken: null,
      companyInfo: null,
    },
    loading: {
      onboardingToken: false,
      companyInfo: false,
    },
  };
}
export default {
  name: "CompanyInfo",
  props: {
    isMobile: {
      type: Boolean,
      default: false,
    },
    client: {
      type: Object,
      required: false,
    },
  },
  components: {
    ImageCropper,
    QrcodeVue,
  },
  data() {
    return initialState();
  },
  created() {
    this.loadClient();
  },
  beforeUnmount() {},
  methods: {
    resetPage() {
      Object.assign(this.$data, initialState());
    },
    loadClient() {
      this.resetPage();

      if (!this.client) {
        console.error("Client not found", this.globalClientId);
        return;
      }
      console.log("Loading client", this.client);

      const { clientId, companyPicUrl, clientName, emailDomain, industry, onboardingToken } =
        this.client;

      this.clientId = clientId;
      this.companyPicUrl = companyPicUrl;
      this.clientName = clientName;
      this.emailDomain = emailDomain;
      this.industry = industry;
      this.onboardingToken = onboardingToken;
    },
    handleImageFile(file) {
      this.companyPicFile = file;
    },
    handleImageUrl(url) {
      this.companyPicUrl = url;
    },
    async handleCopySignupLink() {
      const link = this.formattedSignupLink;
      await navigator.clipboard.writeText(link);

      this.snackbarText = "Copied URL to clipboard: " + link;
      this.dialog.snackbar = true;
    },
    handleEditSignupLink() {
      this.onboardingTokenDraft = this.onboardingToken;
      this.dialog.onboardingToken = true;
    },
    async handleSaveOnboardingLink() {
      if (!this.form.validOnboardingLink) return;

      try {
        this.loading.onboardingToken = true;
        this.error.onboardingToken = null;

        // They want to wipe the link
        if (this.onboardingTokenDraft === "") this.onboardingTokenDraft = null;

        await this.clientService.updateClientV3(this.clientId, {
          onboardingToken: this.onboardingTokenDraft,
        });

        console.log("onboarding link saved. Refreshing client", this.clientId);

        // Refresh to get the new onboarding token
        await this.$emit("getClient", { clientId: this.clientId });

        this.dialog.onboardingToken = false;
        this.onboardingToken = this.userProfile?.Client?.onboardingToken;
        this.onboardingTokenDraft = null;
      } catch (e) {
        this.error.onboardingToken = e.message || "Failed to save link";
      } finally {
        this.loading.onboardingToken = false;
      }
    },
    async handleSaveCompanyInfo() {
      if (!this.form.validCompanyInfo) return;

      try {
        this.loading.companyInfo = true;
        this.error.companyInfo = null;

        let body = {
          clientName: this.clientName,
          emailDomain: this.emailDomain,
          industry: this.industry,
          companyPicUrl: this.companyPicUrl,
        };
        // Wipe so we don't accidentally have both set
        if (this.companyPicFile) delete body.companyPicUrl;

        await this.clientService.updateClientV3(
          this.clientId,
          body
          // this.companyPicFile
        );

        console.log("company info saved", this.clientId);

        if (this.companyPicFile) {
          console.log("uploading company logo");
          await this.clientService.uploadClientImageV3(this.clientId, this.companyPicFile);
        } else if (this.deletedCompanyPic) {
          console.log("deleting company logo");
          await this.clientService.deleteClientImageV3(this.clientId);
        }

        // We'll do a sloppy refresh of the client by pasting in the info
        // Replace this with a proper refresh once we have a proper refresh in the client microservice
        // this.userProfile.Client = {
        //   ...this.userProfile.Client,
        //   clientName: this.clientName,
        //   emailDomain: this.emailDomain,
        //   industry: this.industry
        // };
        await this.$emit("getClient", { clientId: this.clientId });
      } catch (e) {
        // File size error
        if (e?.response?.data?.error_code === "1250168") {
          this.error.companyInfo = e?.response?.data?.error;
        } else {
          this.error.companyInfo = e.message || "Failed to save company info";
        }
      } finally {
        this.loading.companyInfo = false;
      }
    },
    handleLoadImageSelector() {
      if (this.isWhitelabeled) return;
      this.$refs?.uploader?.click();
    },
    handleFileChange(e, dragEvent = false) {
      console.log("changed image");
      const file = dragEvent ? e?.dataTransfer?.files?.[0] : e?.target?.files?.[0];

      if (file) {
        if (!file?.type?.startsWith("image/")) {
          this.error.companyInfo = "Invalid file type";
          return;
        }

        this.companyPicCropUrl = URL.createObjectURL(file);
        this.companyPicFileName = file.name;
        this.dialog.imageCropper = true;

        console.log("file.type", file.type);
        this.imageIsPNGTemp = file.type == "image/png";

        if (document.querySelector("#uploader")) document.querySelector("#uploader").value = "";
      }
    },
    handleSaveCroppedImage(e) {
      console.log("saveCroppedImage", { e });
      this.imageIsPNG = this.imageIsPNGTemp;
      this.resetImageState();
      const { file, imageUrl } = e;
      this.companyPicFile = file;
      this.companyPicUrl = imageUrl;
      this.deletedCompanyPic = false;
    },
    resetImageState(closedCropper = false) {
      this.companyPicCropUrl = null;
      this.companyPicFileName = null;
      this.imageIsPNGTemp = false;
      this.dialog.imageCropper = false;

      if (closedCropper) return;

      // Anything else we need to clear only fter processing
      this.companyPicFile = null;
      this.companyPicUrl = null;
      this.deletedCompanyPic = true;
    },
  },
  computed: {
    ...mapState(["userProfile", "clients", "globalClientId"]),
    isWhitelabeled() {
      return this.client?.whitelabel === true;
    },
    // clientId: {
    //   get: function() {
    //     return this.globalClientId;
    //   }
    // },
    formattedSignupLink() {
      if (this.onboardingToken) {
        return `${window.location.origin}/signup/${this.onboardingToken}`;
      }
      return null;
    },
    formattedSignupLinkEditor() {
      if (this.onboardingTokenDraft) {
        return `${window.location.origin}/signup/${this.onboardingTokenDraft}`;
      }
      return null;
    },
  },
  watch: {
    // globalClientId: {
    //   handler() {
    //     console.log("globalClientId changed", this.globalClientId);
    //     this.loadClient();
    //   }
    // }
    client: {
      handler() {
        console.log("client changed", this.client);
        this.loadClient();
      },
    },
  },
};
</script>

<style scoped></style>
